import { TFunction } from 'i18next';
import Config from 'services/Config/configService';
import { isEmpty } from 'utils/string';
import { IntegrationConnection } from './integrationConnection';
import { IntegrationTypeHandler } from './integrationTypeHandler';

//Mirrors IntegrationType.IntegrationConnectionTypes in the API: the authorization an integration needs (one app
//registration or one credential form). Every type of one connection type shares one connection per tenant.
export enum IntegrationConnectionTypes {
  None = 0,
  EntraMFA = 1,
  SecureScoreM365 = 2,
  SecureScoreIdentity = 3,
  SecureScoreDevice = 4,
  SslLabs = 5,
  //the policy reading app of the Entra baseline checks: Policy.Read.All only
  EntraPolicy = 6,
  //7 is reserved for the Intune reporting app
  //the customer's Azure, read as an access identity the customer creates there (no app registration)
  AzureResources = 8,
  //the ISOPlanner GitHub App, one installation per organization; every GitHub type shares it (API Docs/GitHub_Baseline.md)
  GitHub = 9,
  //the second GitHub App, the code app: Contents and Metadata read, for the CODEOWNERS check only (PLAN-FBL-003)
  GitHubCode = 10,
  //the HubSpot family (API Docs/HubSpot.md): the OAuth grant of the customer's HubSpot manager
  HubSpot = 11,
}

//How Connect GitHub reaches the installation: install the app, or sign in on an organization that already has it
export enum GitHubConnectModes {
  Install = 0,
  SignIn = 1,
}

export enum IntegrationAuthModes {
  None = 0,
  EntraAdminConsent = 1,
  CustomForm = 2,
  //the customer's organization installs ISOPlanner's GitHub App; GitHub returns to Settings > Automation, which completes it
  GitHubAppInstall = 3,
  //an OAuth 2.0 authorization-code grant the API holds per tenant: a popup on the app's consent page, no form
  OAuthGrant = 4,
}

export class IntegrationConnectionTypeHandler {
  integrationConnectionTypeId: IntegrationConnectionTypes;

  name: string;

  description: string;

  clientId: string; //app registration client id

  authMode: IntegrationAuthModes;

  customAuthButtonText?: string;

  customAuthEditButtonText?: string;

  //what the app registration may read, as the registrations in Entra carry it: the application permission names
  //(technical identifiers, not translated); empty for a form without an app registration
  permissions: string[];

  //a cloud connection of the setup flow (setup link, access state, a role that follows the checks that are on)
  hasCloudSetup: boolean;

  constructor() {
    this.integrationConnectionTypeId = IntegrationConnectionTypes.None;
    this.name = '';
    this.description = '';
    this.clientId = '';
    this.authMode = IntegrationAuthModes.None;
    this.permissions = [];
    this.hasCloudSetup = false;
  }

  getScopes(): string[] {
    return ['offline_access', `${this.clientId}/.default`];
  }

  needsAuth(): boolean {
    return this.authMode !== IntegrationAuthModes.None;
  }

  usesAdminConsent(): boolean {
    return this.authMode === IntegrationAuthModes.EntraAdminConsent && !isEmpty(this.clientId);
  }

  usesCustomAuth(): boolean {
    return this.authMode === IntegrationAuthModes.CustomForm;
  }

  usesOAuthGrant(): boolean {
    return this.authMode === IntegrationAuthModes.OAuthGrant;
  }

  usesGitHubAppInstall(): boolean {
    return this.authMode === IntegrationAuthModes.GitHubAppInstall;
  }

  //the authorization starts in the connection type's own modal (IntegrationAuthRenderer): a credential form or the
  //GitHub App install
  usesAuthModal(): boolean {
    return this.usesCustomAuth() || this.usesGitHubAppInstall();
  }
}

export function getIntegrationConnectionTypes(t: TFunction<string[]>): IntegrationConnectionTypeHandler[] {
  const connectionTypes: IntegrationConnectionTypeHandler[] = [];

  const ict_EntraMFA = new IntegrationConnectionTypeHandler();
  ict_EntraMFA.integrationConnectionTypeId = IntegrationConnectionTypes.EntraMFA;
  ict_EntraMFA.name = t('integrations:Connections.Types.EntraMFA.Name');
  ict_EntraMFA.description = t('integrations:Connections.Types.EntraMFA.Description');
  ict_EntraMFA.clientId = Config.get('EntraMFA.ClientId');
  ict_EntraMFA.authMode = IntegrationAuthModes.EntraAdminConsent;
  ict_EntraMFA.permissions = ['AuditLog.Read.All', 'GroupMember.Read.All', 'Policy.Read.All', 'User.ReadBasic.All'];
  connectionTypes.push(ict_EntraMFA);

  const ict_SecureScoreM365 = new IntegrationConnectionTypeHandler();
  ict_SecureScoreM365.integrationConnectionTypeId = IntegrationConnectionTypes.SecureScoreM365;
  ict_SecureScoreM365.name = t('integrations:Connections.Types.SecureScoreM365.Name');
  ict_SecureScoreM365.description = t('integrations:Connections.Types.SecureScoreM365.Description');
  ict_SecureScoreM365.clientId = Config.get('SecureScoreM365.ClientId');
  ict_SecureScoreM365.authMode = IntegrationAuthModes.EntraAdminConsent;
  ict_SecureScoreM365.permissions = ['SecurityEvents.Read.All'];
  connectionTypes.push(ict_SecureScoreM365);

  const ict_SecureScoreIdentity = new IntegrationConnectionTypeHandler();
  ict_SecureScoreIdentity.integrationConnectionTypeId = IntegrationConnectionTypes.SecureScoreIdentity;
  ict_SecureScoreIdentity.name = t('integrations:Connections.Types.SecureScoreIdentity.Name');
  ict_SecureScoreIdentity.description = t('integrations:Connections.Types.SecureScoreIdentity.Description');
  ict_SecureScoreIdentity.clientId = Config.get('SecureScoreIdentity.ClientId');
  ict_SecureScoreIdentity.authMode = IntegrationAuthModes.EntraAdminConsent;
  ict_SecureScoreIdentity.permissions = ['DirectoryRecommendations.Read.All'];
  connectionTypes.push(ict_SecureScoreIdentity);

  const ict_SecureScoreDevice = new IntegrationConnectionTypeHandler();
  ict_SecureScoreDevice.integrationConnectionTypeId = IntegrationConnectionTypes.SecureScoreDevice;
  ict_SecureScoreDevice.name = t('integrations:Connections.Types.SecureScoreDevice.Name');
  ict_SecureScoreDevice.description = t('integrations:Connections.Types.SecureScoreDevice.Description');
  ict_SecureScoreDevice.clientId = Config.get('SecureScoreDevice.ClientId');
  ict_SecureScoreDevice.authMode = IntegrationAuthModes.EntraAdminConsent;
  ict_SecureScoreDevice.permissions = ['Score.Read.All', 'SecurityRecommendation.Read.All'];
  connectionTypes.push(ict_SecureScoreDevice);

  const ict_SslLabs = new IntegrationConnectionTypeHandler();
  ict_SslLabs.integrationConnectionTypeId = IntegrationConnectionTypes.SslLabs;
  ict_SslLabs.name = t('integrations:Connections.Types.SslLabs.Name');
  ict_SslLabs.description = t('integrations:Connections.Types.SslLabs.Description');
  ict_SslLabs.authMode = IntegrationAuthModes.CustomForm;
  ict_SslLabs.customAuthButtonText = t('integrations:SslLabs.RegisterButtonText');
  ict_SslLabs.customAuthEditButtonText = t('integrations:SslLabs.EditRegistrationButtonText');
  connectionTypes.push(ict_SslLabs);

  const ict_HubSpot = new IntegrationConnectionTypeHandler();
  ict_HubSpot.integrationConnectionTypeId = IntegrationConnectionTypes.HubSpot;
  ict_HubSpot.name = t('integrations:Connections.Types.HubSpot.Name');
  ict_HubSpot.description = t('integrations:Connections.Types.HubSpot.Description');
  ict_HubSpot.authMode = IntegrationAuthModes.OAuthGrant;
  ict_HubSpot.customAuthButtonText = t('integrations:HubSpot.AuthButtonText');
  ict_HubSpot.customAuthEditButtonText = t('integrations:HubSpot.EditAuthButtonText');
  connectionTypes.push(ict_HubSpot);

  const ict_EntraPolicy = new IntegrationConnectionTypeHandler();
  ict_EntraPolicy.integrationConnectionTypeId = IntegrationConnectionTypes.EntraPolicy;
  ict_EntraPolicy.name = t('integrations:Connections.Types.EntraPolicy.Name');
  ict_EntraPolicy.description = t('integrations:Connections.Types.EntraPolicy.Description');
  ict_EntraPolicy.clientId = Config.get('EntraPolicy.ClientId');
  ict_EntraPolicy.authMode = IntegrationAuthModes.EntraAdminConsent;
  ict_EntraPolicy.permissions = ['Policy.Read.All'];
  connectionTypes.push(ict_EntraPolicy);

  const ict_AzureResources = new IntegrationConnectionTypeHandler();
  ict_AzureResources.integrationConnectionTypeId = IntegrationConnectionTypes.AzureResources;
  ict_AzureResources.name = t('integrations:Connections.Types.AzureResources.Name');
  ict_AzureResources.description = t('integrations:Connections.Types.AzureResources.Description');
  //a form like SSL Labs, without an app registration: the access to the customer's Azure, then what ISOPlanner checks
  ict_AzureResources.authMode = IntegrationAuthModes.CustomForm;
  ict_AzureResources.customAuthButtonText = t('integrations:MS_Azure.Form.SetUpButton');
  ict_AzureResources.customAuthEditButtonText = t('integrations:MS_Azure.Form.ChangeButton');
  ict_AzureResources.hasCloudSetup = true;
  connectionTypes.push(ict_AzureResources);
  const ict_GitHub = new IntegrationConnectionTypeHandler();
  ict_GitHub.integrationConnectionTypeId = IntegrationConnectionTypes.GitHub;
  ict_GitHub.name = t('integrations:Connections.Types.GitHub.Name');
  ict_GitHub.description = t('integrations:Connections.Types.GitHub.Description');
  ict_GitHub.authMode = IntegrationAuthModes.GitHubAppInstall;
  ict_GitHub.customAuthButtonText = t('integrations:GitHub.ConnectButtonText');
  ict_GitHub.customAuthEditButtonText = t('integrations:GitHub.ReconnectButtonText');
  connectionTypes.push(ict_GitHub);

  const ict_GitHubCode = new IntegrationConnectionTypeHandler();
  ict_GitHubCode.integrationConnectionTypeId = IntegrationConnectionTypes.GitHubCode;
  ict_GitHubCode.name = t('integrations:Connections.Types.GitHubCode.Name');
  ict_GitHubCode.description = t('integrations:Connections.Types.GitHubCode.Description');
  ict_GitHubCode.authMode = IntegrationAuthModes.GitHubAppInstall;
  ict_GitHubCode.customAuthButtonText = t('integrations:GitHub.Code.ConnectButtonText');
  ict_GitHubCode.customAuthEditButtonText = t('integrations:GitHub.Code.ReconnectButtonText');
  connectionTypes.push(ict_GitHubCode);

  return connectionTypes;
}

//The fallback covers a connection type this build does not know yet.
export function getIntegrationConnectionTypeName(
  connectionTypes: IntegrationConnectionTypeHandler[],
  integrationConnectionTypeId: IntegrationConnectionTypes,
  fallback: string,
): string {
  return connectionTypes.find((c) => c.integrationConnectionTypeId === integrationConnectionTypeId)?.name ?? fallback;
}

//A connection is shared, so a result for one integration applies to every integration that uses it. Returns a new
//array where each handler using the connection is cloned with the connection replaced; the others are kept as is,
//so only the changed handlers re-render.
export function applyConnectionToTypes(
  types: IntegrationTypeHandler[],
  connection: IntegrationConnection,
): IntegrationTypeHandler[] {
  return types.map((type) => {
    if (type.integration?.integrationConnectionId !== connection.integrationConnectionId) {
      return type;
    }

    const clone = type.clone();
    if (clone.integration) {
      clone.integration.connection = connection.clone();
    }

    return clone;
  });
}
